<template>
  <div class="media">
    <router-link v-if="canEdit" class="header-btn" to="/media/create" tag="button">{{ $t('buttons.uploadMedia') }}</router-link>
    <div class="search-box">
      <input v-model="searchQuery" type="search" :placeholder="$t('labels.search').toUpperCase()">
      <span>
        <icon-search/>
      </span>
    </div>
    <h1>{{ $t('nav.media') }}</h1>
    <div class="table-header">
      <div class="icons">
        <div :class="{ icon: true, active: viewMode == 'grid' }" @click="viewMode = 'grid'">
          <icon-grid/>
          <span>{{ $t('buttons.grid') }}</span>
        </div>
        <div :class="{ icon: true, active: viewMode == 'table' }" @click="viewMode = 'table'">
          <icon-table/>
          <span>{{ $t('buttons.table') }}</span>
        </div>
      </div>
      <div class="right">
        <div class="country-toggles">
          <ToggleSwitch
            class="toggle-option"
            label="US"
            v-model="showUSA"
          />
          <ToggleSwitch
            class="toggle-option"
            label="CA"
            v-model="showCAN"
          />
        </div>
        <div v-if="viewMode == 'grid'" class="sort">
          <label>{{ $t('media.sortBy') }}:</label>
          <multiselect
            v-model="sortField"
            :options="sortOptions"
            track-by="label"
            label="label"
            :searchable="false"
            :show-labels="false"
            :allow-empty="false"
          />
        </div>
      </div>
    </div>
    <div class="grid" v-if="viewMode == 'grid'">
      <transition-group name="item-list" tag="div">
        <div :class="['item', 'item-' + item.id]" v-for="(item, index) in media" :key="item.id">
          <div class="item-container">
            <div class="thumbs">
              <div class="thumb-wrapper" ref="thumbs">
                <div class="thumb" v-for="(image, index) in item.images" :key="index">
                  <div class="image" v-if="image['4x3'] || image['16x9']">
                    <img :src="image['4x3'] || image['16x9']" alt>
                  </div>
                  <div class="screen">{{ screenNameById(index) }} {{ item.images.length }}</div>
                </div>
              </div>
              <div class="thumb-nav" v-if="Object.keys(item.images).length > 1">
                <div class="thumb-prev" @click="previousThumb(index)"></div>
                <div class="thumb-next" @click="nextThumb(index)"></div>
              </div>
            </div>
            <div class="item-content">
              <h2>{{ item.displayName || item.name || '&nbsp;' }}</h2>
              <div class="flag">
                <IconFlagUS v-if="item.country === 'USA'" />
                <IconFlagCA v-else-if="item.country === 'CAN'" />
              </div>
              <div :class="['status', item.status]">
                <span v-if="item.status == 'incomplete'">{{ $t('status.incomplete') }}</span>
                <span v-else-if="item.status == 'in-queue'">{{ $t('status.inQueue') }}</span>
                <span v-else-if="item.status == 'live'">{{ $t('status.live') }}</span>
                <span v-else-if="item.status == 'archived'">{{ $t('status.archived') }}</span>
              </div>
              <div class="actions">
                <template v-if="canEdit">
                  <router-link v-if="item.status == 'incomplete'" :to="resumeLink(item)">{{ $t('buttons.resume') }}</router-link>
                  <router-link v-else-if="item.status == 'in-queue' || item.status == 'archived'" :to="`/media/${item.id}/review`">{{ $t('buttons.edit') }} &amp; {{ $t('buttons.save') }}</router-link>
                  <router-link v-else-if="!item.default && (item.status == 'live')" :to="`/media/${item.id}/review`" @click.prevent="warningModal">{{ $t('buttons.edit') }} &amp; {{ $t('buttons.republish') }}</router-link>
                  <a href="#" v-if="!item.default" @click.prevent="confirmDelete(item)">{{ $t('buttons.delete') }}</a>
                  <router-link v-else to="/media/default">{{ $t('buttons.edit') }}</router-link>
                </template>
                <template v-else>
                  <router-link v-if="item.default" to="/media/default">{{ $t('buttons.view') }}</router-link>
                  <router-link v-else :to="`/media/${item.id}/review`">{{ $t('buttons.view') }}</router-link>
                </template>
              </div>
              <div class="details">
                <div v-if="item.created" class="detail">
                  {{ $t('media.createdOn').toUpperCase() }}:
                  <span>{{ item.created | date }}</span>
                </div>
                <div v-if="item.userId" class="detail">
                  {{ $t('media.createdBy').toUpperCase() }}:
                  <span>{{ item.firstName }} {{ item.lastName }}</span>
                </div>
                <div v-if="item.platformId" class="detail">
                  {{ $t('media.uploadTo').toUpperCase() }}:
                  <span>{{ item.platformId | platform }}</span>
                </div>
                <div v-if="item.enterpriseName" class="detail">
                  {{ $t('tags.sitehost').toUpperCase() }}:
                  <span>{{ item.enterpriseName }}</span>
                </div>
                <div v-if="item.partnerName" class="detail">
                  {{ $t('tags.partners').toUpperCase() }}:
                  <span>{{ item.partnerName }}</span>
                </div>
                <div v-if="item.evseName" class="detail">
                  {{ $t('media.chargers').toUpperCase() }}:
                  <span>{{ item.evseName }}</span>
                </div>
                <div v-if="item.locationName" class="detail">
                  {{ $t('media.stations').toUpperCase() }}:
                  <span>{{ item.locationName }}</span>
                </div>
                <div v-if="item.stateName" class="detail">
                  {{ $t('media.states').toUpperCase() }}:
                  <span>{{ item.stateName }}</span>
                </div>
                <div v-if="item.zipName" class="detail">
                  {{ $t('media.zipCodes').toUpperCase() }}:
                  <span>{{ item.zipName }}</span>
                </div>
                <div v-if="item.liveDate" class="detail">
                  {{ $t('media.goLiveDate').toUpperCase() }}:
                  <span>{{ item.liveDate | date }}</span>
                </div>
                <div v-if="item.expirationDate" class="detail">
                  {{ $t('media.expirationDateAbbr').toUpperCase() }}:
                  <span>{{ item.expirationDate | date }}</span>
                </div>
                <div v-if="item.duration" class="detail">
                  {{ $t('media.duration').toUpperCase() }}:
                  <span>{{ item.duration }} seconds</span>
                </div>
                <div v-if="item.cost" class="detail">
                  {{ $t('media.cost').toUpperCase() }}:
                  <span>{{ item.cost | currency }}</span>
                </div>
                <div v-if="item.comments" class="detail">
                  {{ $t('media.comments').toUpperCase() }}:
                  <span>{{ item.comments }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition-group>
    </div>
    <data-table
      v-else
      :headings="headings"
      :rows="media"
      :lazy-load="true"
      :search-query="searchQuery"
    >
      <template v-slot:countrySlot="slotProps">
        <IconFlagUS class="flag" v-if="slotProps.row.country === 'USA'" />
        <IconFlagCA class="flag" v-else-if="slotProps.row.country === 'CAN'" />
      </template>
      <template v-slot:linksSlot="slotProps">
        <template v-if="canEdit">
          <router-link v-if="slotProps.row.status == 'incomplete'" :to="resumeLink(slotProps.row)">{{ $t('buttons.resume') }}</router-link>
          <router-link v-else-if="slotProps.row.status == 'in-queue' || slotProps.row.status == 'archived'" :to="`/media/${slotProps.row.id}/review`">{{ $t('buttons.edit') }} &amp; {{ $t('buttons.save') }}</router-link>
          <router-link v-else-if="!slotProps.row.default && (slotProps.row.status == 'live')" :to="`/media/${slotProps.row.id}/review`">{{ $t('buttons.edit') }} &amp; {{ $t('buttons.republish') }}</router-link>
          <a href="#" v-if="!slotProps.row.default" @click.prevent="confirmDelete(slotProps.row)">{{ $t('buttons.delete') }}</a>
          <router-link v-else to="/media/default">{{ $t('buttons.edit') }}</router-link>
        </template>
        <template v-else>
          <router-link v-if="slotProps.row.default" to="/media/default">{{ $t('buttons.view') }}</router-link>
          <router-link v-else :to="`/media/${slotProps.row.id}/review`">{{ $t('buttons.view') }}</router-link>
        </template>
      </template>
      <template v-slot:moreInfoSlot="slotProps">
        <div class="more-info">
          <div class="more-toggle">
            <icon-view-more/>
          </div>
          <div class="more-popup">
            <div v-if="slotProps.row.created" class="detail">
              {{ $t('media.createdOn').toUpperCase() }}:
              <span>{{ slotProps.row.created | date }}</span>
            </div>
            <div v-if="slotProps.row.userId" class="detail">
              {{ $t('media.createdBy').toUpperCase() }}:
              <span>{{ slotProps.row.firstName }} {{ slotProps.row.lastName }}</span>
            </div>
            <div v-if="slotProps.row.platformId" class="detail">
              {{ $t('media.uploadTo').toUpperCase() }}:
              <span>{{ slotProps.row.platformId | platform }}</span>
            </div>
            <!-- <div v-if="slotProps.row.utility" class="detail">
              UTILITY:
              <span>{{ slotProps.row.utility }}</span>
            </div> -->
            <div v-if="slotProps.row.evseName" class="detail">
              {{ $t('media.chargers').toUpperCase() }}:
              <span>{{ slotProps.row.evseName }}</span>
            </div>
            <div v-if="slotProps.row.locationName" class="detail">
              {{ $t('media.stations').toUpperCase() }}:
              <span>{{ slotProps.row.locationName }}</span>
            </div>
            <div v-if="slotProps.row.stateName" class="detail">
              {{ $t('media.states').toUpperCase() }}:
              <span>{{ slotProps.row.stateName }}</span>
            </div>
            <div v-if="slotProps.row.zipName" class="detail">
              {{ $t('media.zipCodes').toUpperCase() }}:
              <span>{{ slotProps.row.zipName }}</span>
            </div>
            <div v-if="slotProps.row.duration" class="detail">
              {{ $t('media.duration').toUpperCase() }}:
              <span>{{ slotProps.row.duration }} seconds</span>
            </div>
            <div v-if="slotProps.row.cost" class="detail">
              {{ $t('media.cost').toUpperCase() }}:
              <span>{{ slotProps.row.cost | currency }}</span>
            </div>
            <div v-if="slotProps.row.comments" class="detail">
              {{ $t('media.comments').toUpperCase() }}:
              <span>{{ slotProps.row.comments }}</span>
            </div>
          </div>
        </div>
      </template>
    </data-table>
    <back-to-top/>
    <modal class="delete-modal" v-show="showDeleteModal">
      <template v-slot:body>
        <p>{{ $t('media.deleteModal') }}</p>
      </template>
      <template v-slot:footer>
        <button @click.stop="cancelDelete()" class="alt-btn no-btn">{{ $t('buttons.noGoBack').toUpperCase() }}</button>
        <button @click.stop="deleteMedia(delItem)">{{ $t('buttons.yesDelete').toUpperCase() }}</button>
      </template>
    </modal>
    <modal class="warning-modal" v-show="showWarningModal" @close="showWarningModal = false">
      <template v-slot:body>
        <p>{{ $t('media.warningModal') }}</p>
      </template>
      <template v-slot:footer>
        <button class="alt-btn" @click="showWarningModal = false">{{ $t('buttons.cancel') }}</button>
        <button @click="$options.nextFn()">{{ $t('buttons.editMedia') }}</button>
      </template>
    </modal>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import BackToTop from '@/components/BackToTop.vue'
import Modal from '@/components/Modal.vue'
import { GET_MEDIA, GET_DEFAULT_MEDIA, DELETE_MEDIA, GET_CRITERIA } from '@/store/actions.type'
import IconSearch from '@/assets/images/icons/search_icon.svg'
import IconGrid from '@/assets/images/icons/grid_icon.svg'
import IconTable from '@/assets/images/icons/table_icon.svg'
import IconViewMore from '@/assets/images/icons/view_more_icon.svg'
import IconFlagUS from '@/assets/images/icons/flag_us.svg'
import IconFlagCA from '@/assets/images/icons/flag_ca.svg'
import ToggleSwitch from '@/components/ToggleSwitch.vue'
import { mapState } from 'vuex'

export default {
  name: 'media',
  components: {
    DataTable,
    Multiselect,
    BackToTop,
    Modal,
    IconSearch,
    IconGrid,
    IconTable,
    IconViewMore,
    IconFlagUS,
    IconFlagCA,
    ToggleSwitch,
  },
  nextFn: function() {},
  data() {
    return {
      showDeleteModal: false,
      delItem: null,
      showWarningModal: false,
      searchQuery: '',
      viewMode: 'grid',
      sortField: null,
      showUSA: true,
      showCAN: true,
    }
  },
  computed: {
    ...mapState({
      partners: state => state.partners.partnerData,
      sitehosts: state => state.media.sitehost,
      stations: state => state.media.stationId,
      chargers: state => state.media.chargerId,
      states: state => state.media.state,
      platforms: state => state.media.platforms,
      utilities: state => state.media.utility,
    }),
    canEdit() {
      return this.$store.getters.canEdit
    },
    media() {
      const defaultMedia = JSON.parse(JSON.stringify(this.$store.state.media.defaultMedia))
      let media = this.$store.getters.media.map(value => {
        const newProps = {}
        if (value.images) {
          newProps.images = {}
          for (const language in value.images) {
            if (value.images.hasOwnProperty(language)) {
              const screens = value.images[language]
              for (const screen in screens) {
                if (screens.hasOwnProperty(screen)) {
                  const positions = screens[screen]
                  for (const pos in positions) {
                    if (positions.hasOwnProperty(pos) && !newProps.images[screen]) {
                      newProps.images[screen] = positions[pos]
                    }
                  }
                }
              }
            }
          }
          const languageScreens = value.images[Object.keys(value.images)[0]]
          if (languageScreens) {
            const position = languageScreens[Object.keys(languageScreens)[0]]
            if (position) {
              const firstScreen = position[Object.keys(position)[0]]
              if (firstScreen) {
                newProps.preview = firstScreen[Object.keys(firstScreen)[0]]
              }
            }
          }
        }
        if (!value.published) {
          newProps.status = 'incomplete'
        } else {
          const today = new Date()
          const now = today.getTime()
          if (value.liveDate) {
            if (value.liveDate.getTime() <= now) {
              if (!value.expirationDate || value.expirationDate.getTime() >= now) {
                newProps.status = 'live'
              } else {
                newProps.status = 'archived'
              }
            } else {
              newProps.status = 'in-queue'
            }
          } else {
            newProps.status = 'incomplete'
          }
        }
        if (value.partnerId && this.partners.hasOwnProperty(value.partnerId)) {
          newProps.partnerName = this.partners[value.partnerId].name
        }
        if (value.enterpriseId && this.sitehosts.hasOwnProperty(value.enterpriseId)) {
          newProps.enterpriseName = this.sitehosts[value.enterpriseId].name
        }
        if (value.utilityId && this.utilities.hasOwnProperty(value.utilityId)) {
          newProps.utility = this.utilities[value.utilityId].name;
        }
        if (value.platformId && this.platforms.hasOwnProperty(value.platformId)) {
          newProps.platform = this.platforms[value.platformId];
        }
        if (value.evseId && Array.isArray(value.evseId) && value.evseId.length) {
          newProps.evseName = value.evseId.map(evseId => {
            if (this.chargers.hasOwnProperty(evseId)) {
              return this.chargers[evseId].name
            }
          }).filter(el => el).join(', ')
        }
        if (value.locationId && Array.isArray(value.locationId) && value.locationId.length) {
          newProps.locationName = value.locationId.map(locationId => {
            if (this.stations.hasOwnProperty(locationId)) {
              return this.stations[locationId].name
            }
          }).filter(el => el).join(', ')
        }
        if (value.stateCode && Array.isArray(value.stateCode) && value.stateCode.length) {
          newProps.stateName = value.stateCode.map(stateCode => {
            if (this.states.hasOwnProperty(stateCode)) {
              return this.states[stateCode].name
            }
          }).filter(el => el).join(', ')
        }
        if (value.zipCode && Array.isArray(value.zipCode) && value.zipCode.length) {
          newProps.zipName = value.zipCode.join(', ')
        }
        return Object.assign({}, value, newProps)
      })
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        media = media.filter(image => {
          for (const key in image) {
            if (
              image.hasOwnProperty(key) &&
              image[key] &&
              image[key]
                .toString()
                .toLowerCase()
                .indexOf(query) > -1
            )
              return true
          }
          return false
        })
      }
      if (!this.showUSA) media = media.filter(item => item.country === 'CAN');
      if (!this.showCAN) media = media.filter(item => item.country === 'USA');
      if (this.showUSA && defaultMedia.US) {
        defaultMedia.US.images = defaultMedia.US.images['en-US'] || {}
        media.push(defaultMedia.US)
      }
      if (this.showCAN && defaultMedia.CA) {
        defaultMedia.CA.images = defaultMedia.CA.images['en-CA'] || {}
        media.push(defaultMedia.CA)
      }
      media.sort((a, b) => {
        if (a.default) return -1
        else if (b.default) return 1

        const key = this.sortField.key
        // console.log(a[key], b[key])
        if (!a.hasOwnProperty(key) || !a[key]) return 1
        else if (!b.hasOwnProperty(key) || !b[key]) return -1
        else {
          // console.log(a[key], b[key], a[key].toLowerCase().localeCompare(b[key].toLowerCase()))
          if (this.sortField.order == 'desc')
            return a[key].toLowerCase().localeCompare(b[key].toLowerCase())
          else return b[key].toLowerCase().localeCompare(a[key].toLowerCase())
        }
      })
      if (this.$route.query.status) {
        media = media.filter(item => item.status === this.$route.query.status)
      } else if (this.$route.query.expire) {
        const days = parseInt(this.$route.query.expire)
        if (days) {
          media = media.filter(item => {
            if (item.expirationDate) {
              const date = new Date(item.expirationDate)
              const now = new Date()
              if (date) {
                const diff = date.getTime() - now.getTime()
                if (diff > 0 && diff < days * 86400000) {
                  return true
                }
              }
            }
            return false
          })
        }
      } else if (this.$route.query.live) {
        const days = parseInt(this.$route.query.live)
        if (days) {
          media = media.filter(item => {
            if (item.liveDate) {
              const date = new Date(item.liveDate)
              const now = new Date()
              if (date) {
                const diff = date.getTime() - now.getTime()
                if (diff > 0 && diff < days * 86400000) {
                  return true
                }
              }
            }
            return false
          })
        }
      }
      return media
    },
    sortOptions() {
      return [
        {
          label: this.$t('media.name') + ' (A-Z)',
          key: 'name',
          order: 'desc'
        },
        {
          label: this.$t('media.name') + ' (Z-A)',
          key: 'name',
          order: 'asc'
        },
        {
          label: this.$t('media.status') + ' (ASC)',
          key: 'status',
          order: 'asc'
        },
        {
          label: this.$t('media.status') + ' (DESC)',
          key: 'status',
          order: 'desc'
        },
        {
          label: this.$t('tags.sitehost'),
          key: 'siteHost',
          order: 'asc'
        },
        {
          label: this.$t('tags.partners'),
          key: 'partnerName',
          order: 'asc'
        },
        // {
        //   label: 'Utility',
        //   key: 'utility',
        //   order: 'asc'
        // },
        {
          label: this.$t('media.goLiveDate') + ' (ASC)',
          key: 'liveDate',
          order: 'asc'
        },
        {
          label: this.$t('media.goLiveDate') + ' (DESC)',
          key: 'liveDate',
          order: 'desc'
        },
        {
          label: this.$t('media.expirationDate') + ' (ASC)',
          key: 'expirationDate',
          order: 'asc'
        },
        {
          label: this.$t('media.expirationDate') + ' (DESC)',
          key: 'expirationDate',
          order: 'desc'
        }
      ]
    },
    headings() {
      return [
        {
          label: this.$t('media.preview'),
          key: 'preview',
          type: 'image',
          sortable: false
        },
        {
          label: this.$t('media.name'),
          key: 'name'
        },
        {
          label: this.$t('media.status'),
          key: 'status',
          keyClass: true
        },
        {
          label: this.$t('media.uploadTo'),
          key: 'platform'
        },
        {
          label: this.$t('tags.sitehost'),
          key: 'enterpriseName'
        },
        {
          label: this.$t('media.utility'),
          key: 'utility'
        },
        {
          label: this.$t('tags.partners'),
          key: 'partnerName'
        },
        {
          label: this.$t('media.goLiveDate'),
          key: 'liveDate',
          sortType: 'date',
          filter: 'date'
        },
        {
          label: this.$t('media.expirationDateAbbr'),
          key: 'expirationDate',
          filter: 'date'
        },
        {
          label: this.$t('media.audience'),
          key: 'country',
        },
        {
          label: '',
          key: 'links',
          sortable: false
          // key: 'evseDisplayId'
        },
        {
          label: '',
          key: 'moreInfo',
          sortable: false
        }
      ]
    }
  },
  methods: {
    previousThumb(index) {
      const thumb = this.$refs.thumbs[index]
      if (thumb) {
        const count = thumb.childNodes.length
        let pos = Math.abs(parseInt(thumb.style.left || 0, 10) / 100) - 1
        if (pos < 0) pos = count + pos
        thumb.style.left = (pos % count) * -100 + '%'
      }
    },
    nextThumb(index) {
      const thumb = this.$refs.thumbs[index]
      if (thumb) {
        const count = thumb.childNodes.length
        const pos = Math.abs(parseInt(thumb.style.left || 0, 10) / 100) + 1
        thumb.style.left = (pos % count) * -100 + '%'
      }
    },
    resumeLink(item) {
      const path = '/media/' + item.id
      if (!item.name || item.name === '[no-name]') return path + '/name'
      else if (!item.liveDate || !item.expirationDate) return path + '/dates'
      return path + '/details'
    },
    confirmDelete(item) {
      if (item && item.id) {
        this.delItem = item;
        this.showDeleteModal = true;
      }
    },
    cancelDelete() {
      this.delItem = null;
      this.showDeleteModal = false;
    },
    deleteMedia(item) {
      if (item && item.id) {
        this.showDeleteModal = false;
        const loader = this.$loading.show()
        this.$store.dispatch(DELETE_MEDIA, item.id).finally(() => {
          loader.hide();
          this.delItem = null;
        })
      }
    },
    screenNameById(id) {
      const screenText = this.$t('media.screen')
      if (id === '130-00-CMS (Screensaver)') return screenText + ' 1'
      else if (id === '130-50M-CMS (Present Payment (member))') return screenText + ' 2'
      else if (id === '170-10-CMS (Show Price)') return screenText + ' 3'
      else if (id === '170-170-CMS (Thank You)') return screenText + ' 4'
    }
  },
  watch: {
    '$i18n.locale': function() {
      if (this.sortField) {
        const field = this.sortOptions.find(opt => opt.key === this.sortField.key)
        if (field) this.sortField = field
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (to.params.media && to.params.media !== 'default') {
      const item = this.media.find(m => m.id == to.params.media)
      if (item && item.status === 'live') {
        this.showWarningModal = true
        this.$options.nextFn = next
      } else {
        next()
      }
    } else {
      next()
    }
  },
  created() {
    this.sortField = this.sortOptions[0]
    const loader = this.$loading.show()
    Promise.all([
      this.$store.dispatch(GET_MEDIA),
      this.$store.dispatch(GET_DEFAULT_MEDIA),
      this.$store.dispatch(GET_CRITERIA)
    ]).finally(() => {
      loader.hide()
    })
  }
}
</script>

<style lang="scss" scoped>
.flag {
  width: 28px;
  height: 14px;
}

.delete-modal {
  p {
    text-align: center;
  }

  .no-btn {
    margin-right: 25px;
  }
}

.item-list-move {
  transition: transform 500ms;
}
.header-btn {
  float: right;
  margin-top: 12px;
  margin-left: 25px;
}
.media {
  box-sizing: border-box;
  width: 100%;
  padding-bottom: 400px !important;
}
.search-box {
  display: flex;
  align-items: flex-end;
  float: right;
  width: 100%;
  max-width: 400px;

  span {
    display: inline-block;
    margin-bottom: em(7);
    max-width: 20px;
    max-height: 20px;

    svg {
      fill: currentColor;
    }
  }
}
.table-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin: 50px 0 18px;

  .icon {
    cursor: pointer;
    display: inline-block;

    & + .icon {
      margin-left: 25px;
    }
    &.active {
      color: #35bdd1;
      font-weight: 600;
    }
    svg {
      width: 1em;
      height: 1em;
      vertical-align: middle;
    }
    span {
      margin-left: 10px;
      vertical-align: middle;
    }
  }

  .right {
    display: flex;
    align-items: center;
  }

  .country-toggles {
    display: flex;
    
    .toggle-option {
      margin-right: 34px;
    }
  }

  .sort {
    display: flex;
    align-items: center;
    margin-left: auto;

    label {
      white-space: nowrap;
      margin-right: 10px;
    }

    /deep/ .multiselect__content-wrapper {
      right: 0;
      width: auto;
      min-width: 100%;
    }
  }
}
.data-table {
  /deep/ {
    .preview {
      position: relative;

      img {
        display: block;
        position: absolute;
        top: 50%;
        left: 35px;
        z-index: 2;
        max-width: 75px;
        max-height: 50px;
        transform: translateY(-50%);
      }

      &::before {
        content: '';
        background: #adadad url('../assets/images/icons/image_icon.svg?external') no-repeat
          center center;
        background-size: 20px auto;
        display: block;
        margin: -14px 0;
        width: 75px;
        height: 48px;
      }
    }

    .status {
      font-weight: 600;
      text-transform: uppercase;

      &.incomplete {
        color: #eb3323;
      }
      &.live {
        color: #acd178;
      }
      &.archived {
        color: #898989;
      }
    }

    .links a + a {
      color: #4399b0;
      margin-left: 25px;
    }

    .moreInfo {
      width: 1px;

      .more-info {
        position: relative;
      }

      .more-toggle {
        width: 6px;
        height: 30px;
      }
      .more-popup {
        background-color: #fff;
        box-shadow: 0 5px 40px #898989;
        margin-right: 11px;
        padding: 45px;
        position: absolute;
        top: -14px;
        right: 100%;
        display: none;
        z-index: 999;

        &::after {
          content: '';
          border-style: solid;
          border-width: 8px 0 8px 9px;
          border-color: transparent transparent transparent #fff;
          position: absolute;
          top: 20px;
          right: -9px;
          width: 0;
          height: 0;
        }

        .detail {
          color: #898989;
          font-weight: 300;
          text-transform: uppercase;
          white-space: nowrap;

          &.comments {
            white-space: normal;
          }

          & + .detail {
            margin-top: 10px;
          }

          span {
            color: #0b0f4d;
            font-weight: 600;
            text-transform: none;
          }
        }
      }
      &:hover .more-popup {
        display: block;
      }
    }
  }
}
.grid {
  margin-left: -25px;

  .item {
    background-color: #fff;
    // box-sizing: border-box;
    float: left;
    margin-left: 25px;
    margin-bottom: 25px;
    padding-bottom: 12.6%;
    position: relative;
    width: calc(20% - 25px);
    // height: 18.5em;
    height: 8.7em;

    &.item-default::after {
      content: '';
      background: url('../assets/images/icons/banner_icon.svg?external') no-repeat center
        center;
      position: absolute;
      top: -10px;
      left: 20px;
      width: 42px;
      height: 59px;
      z-index: 101;
      transition: transform 300ms ease 200ms;
    }

    .item-container {
      padding: 25px;
      position: relative;
      transition: z-index 300ms ease, margin 300ms ease;
      transition-delay: 200ms;
      z-index: 0;

      &::before {
        content: '';
        background-color: #fff;
        box-shadow: 0 5px 10px #898989;
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        visibility: hidden;
        transition: opacity 300ms ease 200ms, visibility 0s ease 500ms;
      }
    }

    .item-content {
      position: relative;

      .flag {
        float: right;

        svg {
          width: 23px;
          height: 14px;
        }
      }
    }

    .thumbs {
      background: #adadad url('../assets/images/icons/image_icon.svg?external') no-repeat
        center center;
      background-size: 68px auto;
      padding-bottom: 215px / 260px * 100%;
      margin: -25px -25px 25px;
      overflow: hidden;
      position: relative;

      .thumb-wrapper {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 1000%;
        transition: left 300ms ease;
      }

      .thumb {
        display: inline-block;
        overflow: hidden;
        position: relative;
        width: 10%;
        height: 100%;

        .image {
          display: flex;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;

          img {
            display: block;
            margin: auto;
            max-width: 100%;
            max-height: 100%;
          }
        }
      }
      .thumb-nav {
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transition: opacity 300ms ease 200ms;

        .thumb-prev,
        .thumb-next {
          cursor: pointer;
          margin-top: -40px;
          position: absolute;
          top: 50%;
          width: 50px;
          height: 50px;

          &::before {
            content: '';
            border-style: solid;
            border-width: 7.5px 8px 7.5px 0;
            border-color: transparent #fff transparent transparent;
            display: block;
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            transform: translate(-50%, -50%);
          }
        }
        .thumb-next {
          right: 0;
          transform: rotate(180deg);
        }
      }
      .screen {
        color: #fff;
        text-transform: uppercase;
        background-color: rgba(11, 15, 77, 0.5);
        opacity: 0;
        padding: 0.4em 25px;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        overflow: hidden;
        transition: opacity 300ms ease 200ms;
      }
    }

    h2 {
      font-size: em(24);
      font-weight: 300;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    .status {
      font-weight: 600;
      text-transform: uppercase;
      margin-bottom: 10px;

      &.incomplete {
        color: #eb3323;
      }
      &.live {
        color: #acd178;
      }
      &.archived {
        color: #898989;
      }
    }

    .details {
      margin-top: 10px;
      overflow: hidden;
      max-height: 0;
      visibility: hidden;
      transition: visibility 0s 500ms, max-height 300ms 500ms;

      .detail {
        color: #898989;
        text-transform: uppercase;
        opacity: 0;
        transition: opacity 300ms ease 200ms;

        span {
          color: #0b0f4d;
          font-weight: 600;
          text-transform: none;
        }
      }
    }
    .actions a + a {
      margin-left: 25px;
      margin-top: 30px;
    }

    &:hover {
      &.item-default::after {
        transform: translate(-25px, -32px);
      }
      .item-container {
        margin: -32px -25px 0;
        z-index: 100;

        &::before {
          opacity: 1;
          visibility: visible;
          transition: opacity 300ms ease 200ms;
        }
      }

      .screen,
      .thumb-nav {
        opacity: 1;
      }

      .details {
        max-height: 500px;
        visibility: visible;
        transition: none;

        .detail {
          opacity: 1;
          transition-delay: 300ms;
        }
      }
    }
  }
}
.warning-modal {
  /deep/ {
    .modal {
      max-width: 510px;

      .modal-footer {
        justify-content: space-between !important;
      }
    }
  }
}
</style>
